import {
  Box,
  Center,
  Flex,
  Highlight,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spacer,
  Spinner,
  Text,
  Tooltip,
  useColorModeValue,
  useOutsideClick,
} from '@chakra-ui/react';
import {
  faAngleDown,
  faAngleRight,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useRef, useState } from 'react';
import SparkelIcon from './icon/SparkelIcon';

type DataItem = {
  label: string;
  children?: DataItem[];
};

type PropertyCascaderProps = React.ComponentProps<typeof Input> & {
  data: DataItem[];
  defaultSelection?: number[];
  onPropertySelect?: (activePath: number[]) => void;
  onCascaderOpen?: () => void;
  onCascaderClose?: () => void;
  closeOnSelect?: boolean;
  isLoading?: boolean;
};

const RightIcon: React.FC<{
  item?: DataItem;
  isActive?: boolean;
  isLoading?: boolean;
}> = ({ item, isActive, isLoading }) => {
  if (item && item.children && item.children.length > 0) {
    return <SparkelIcon icon={faAngleRight} color={['gray.400', 'gray.500']} />;
  }
  if (isActive && isLoading) {
    return <Spinner size={'xs'} />;
  }
  return null;
};

const CascadingMenuItem: React.FC<{
  children?: React.ReactNode;
  item?: DataItem;
  isActive?: boolean;
  isLoading?: boolean;
  onClick: () => void;
}> = ({ children, item, isActive, isLoading, onClick }) => {
  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700');
  const selectedItemTextColor = useColorModeValue('blue.700', 'blue.200');
  const selectedItemBackgroundColor = useColorModeValue(
    'blue.50',
    'rgba(66, 153, 225, 0.24)'
  );

  return (
    <Box
      className="cascadingMenuItem"
      borderRadius={'md'}
      fontWeight={isActive ? 'bold' : undefined}
      color={isActive ? selectedItemTextColor : undefined}
      backgroundColor={isActive ? selectedItemBackgroundColor : undefined}
      _hover={{ backgroundColor: hoverBackgroundColor }}
      onClick={onClick}
      cursor={'pointer'}
      width={'100%'}
      paddingY={1}
      paddingX={2}
      pointerEvents={isLoading || isActive ? 'none' : undefined}
    >
      <Flex width={'100%'}>
        <Text wordBreak={'break-all'} noOfLines={1} maxWidth={'98%'}>
          {children ? children : item ? item.label : ''}
        </Text>
        <Spacer />
        <Center>
          <RightIcon item={item} isActive={isActive} isLoading={isLoading} />
        </Center>
      </Flex>
    </Box>
  );
};

// eslint-disable-next-line complexity
export function PropertyCascader({
  data,
  defaultSelection,
  onPropertySelect,
  onCascaderOpen,
  onCascaderClose,
  isLoading,
  closeOnSelect,
  ...props
}: PropertyCascaderProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cascaderRef = useRef<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const [activePath, setActivePath] = useState<number[]>([]);
  const [selectionConfirmed, setSelectionConfirmed] = useState(false);

  useEffect(() => {
    if (isOpen && defaultSelection && !selectionConfirmed) {
      setActivePath(defaultSelection);
    }
  }, [isOpen, defaultSelection, selectionConfirmed]);

  useOutsideClick({
    ref: cascaderRef,
    handler: () => {
      if (isOpen) {
        setIsOpen(false);
        setActivePath([]);
        setSelectionConfirmed(false);

        if (onCascaderClose) {
          onCascaderClose();
        }
      }
    },
  });

  const getActiveData = (level: number) => {
    let activeData = data;
    for (let i = 0; i < level; i++) {
      if (activePath[i] !== undefined && activeData[activePath[i]]) {
        activeData = activeData[activePath[i]].children || [];
      }
    }
    return activeData;
  };

  const getActiveDataLabel = () => {
    let previousData = data[activePath[0]];
    let activeDataLabel = previousData ? previousData.label : '';

    if (activePath.length > 0) {
      for (let i = 1; i < activePath.length; i++) {
        if (previousData && previousData.children) {
          const childIndex = activePath[i];
          activeDataLabel += ' / ' + previousData.children[childIndex].label;
          previousData = data[childIndex];
        }
      }
    }

    return activeDataLabel;
  };

  type PathItem = {
    path: string[];
    indices: number[];
  };

  const filterData = (
    data: DataItem[],
    term: string,
    currentPath: string[] = [],
    currentIndex: number[] = []
  ): PathItem[] => {
    let results: PathItem[] = [];

    if (term.length < 2) {
      return results;
    }

    data.forEach((item, idx) => {
      const newPath = [...currentPath, item.label];
      const newIndices = [...currentIndex, idx];

      if (item.children) {
        results = results.concat(
          filterData(item.children, term, newPath, newIndices)
        );
      } else {
        if (newPath.some((p) => p.toLowerCase().includes(term))) {
          results.push({ path: newPath, indices: newIndices });
        }
      }
    });

    return results;
  };

  const filteredPaths = searchTerm ? filterData(data, searchTerm) : [];

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const secondaryTextColor = useColorModeValue('gray.400', 'gray.500');

  return (
    <Popover
      closeOnBlur={false}
      isOpen={isOpen}
      variant={'responsive'}
      placement="bottom-start"
      autoFocus={false}
      returnFocusOnClose={false}
      isLazy
    >
      <PopoverTrigger>
        <Box
          onClick={(e) => {
            e.preventDefault();
            if (!isOpen) {
              setIsOpen(true);
              if (onCascaderOpen) onCascaderOpen();
            }
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
        >
          <Tooltip
            label={activePath.length > 0 ? getActiveDataLabel() : undefined}
            openDelay={300}
          >
            <InputGroup size={props.size} {...props}>
              <InputRightElement
                pointerEvents={searchTerm ? undefined : 'none'}
              >
                <SparkelIcon
                  icon={isOpen ? faSearch : faAngleDown}
                  color={['gray.300', 'gray.500']}
                />
              </InputRightElement>

              <Input
                cursor={isOpen ? 'text' : 'pointer'}
                ref={inputRef}
                disabled={isLoading}
                placeholder={props.placeholder}
                _placeholder={props._placeholder}
                borderRadius={'base'}
                value={searchTerm}
                onChange={(e) => {
                  if (!isOpen) {
                    setIsOpen(true);
                    if (onCascaderOpen) onCascaderOpen();
                  }

                  setSearchTerm(e.target.value.toLowerCase());
                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
              />
            </InputGroup>
          </Tooltip>
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          zIndex={99}
          shadow={'lg'}
          width={'max-content'}
          ref={cascaderRef}
          color={isLoading ? 'gray.500' : undefined}
        >
          <Box display="flex" flexDirection="row" fontSize={'xs'} padding={1}>
            {/* First level */}
            <Box
              minWidth={150}
              maxWidth={400}
              marginRight={0}
              padding={1}
              height={176}
              overflow={'scroll'}
            >
              {data && searchTerm && filteredPaths.length > 0 ? (
                filteredPaths.map((item, idx) => (
                  // Render search results
                  <CascadingMenuItem
                    key={idx}
                    isLoading={isLoading}
                    onClick={() => {
                      if (closeOnSelect) {
                        setIsOpen(false);
                      }

                      setActivePath(item.indices);
                      setSearchTerm('');

                      if (onPropertySelect) {
                        onPropertySelect(item.indices);
                      }

                      setSelectionConfirmed(true);
                    }}
                  >
                    {item.path.map((label, labelIdx) => (
                      <React.Fragment key={label}>
                        {labelIdx > 0 && ' / '}
                        <Highlight
                          query={searchTerm}
                          styles={{
                            color: 'blue.300',
                            fontWeight: 'bold',
                          }}
                        >
                          {label}
                        </Highlight>
                      </React.Fragment>
                    ))}
                  </CascadingMenuItem>
                ))
              ) : searchTerm && filteredPaths.length === 0 ? (
                <Text margin={2} color={secondaryTextColor}>
                  No properties found
                </Text>
              ) : undefined}
              {data && !searchTerm
                ? data.map((item, idx) => (
                    // Render first level items
                    <CascadingMenuItem
                      key={idx}
                      isLoading={isLoading}
                      item={item}
                      isActive={activePath[0] === idx}
                      onClick={() => {
                        setActivePath([idx]);
                        if (!item.children || item.children.length === 0) {
                          if (closeOnSelect) {
                            setIsOpen(false);
                          }

                          if (onPropertySelect) {
                            onPropertySelect(activePath);
                          }

                          setSelectionConfirmed(true);
                        }
                        setSearchTerm('');
                      }}
                    />
                  ))
                : undefined}
            </Box>

            {/* Subsequent Levels */}
            {!searchTerm
              ? activePath.map((_, level) =>
                  getActiveData(level + 1).length > 0 ? (
                    <Box
                      key={level}
                      borderLeft="1px solid"
                      borderColor={borderColor}
                      minWidth={150}
                      maxWidth={400}
                      padding={1}
                      height={176}
                      overflow={'scroll'}
                    >
                      {getActiveData(level + 1).map((item, idx) => (
                        <CascadingMenuItem
                          key={idx}
                          isLoading={isLoading}
                          item={item}
                          isActive={activePath[level + 1] === idx}
                          onClick={() => {
                            const newActivePath = [
                              ...activePath.slice(0, level + 1),
                              idx,
                            ];
                            setActivePath(newActivePath);

                            if (!item.children || item.children.length < 0) {
                              if (closeOnSelect) {
                                setIsOpen(false);
                              }

                              if (onPropertySelect) {
                                onPropertySelect(newActivePath);
                              }

                              setSelectionConfirmed(true);
                            }

                            setSearchTerm('');
                          }}
                        />
                      ))}
                    </Box>
                  ) : undefined
                )
              : undefined}
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
